import { cloneDeep, matchesProperty, merge, negate } from "lodash";
import { defineStore } from "pinia";
import api, { instance } from "@/api";
import { keycloak } from "@/utilities/keycloak";
import { LocalStorage } from "@/storage";
import router from "@/router";

const profileAgreementModel = {
  agreement: {
    description: null,
    id: null,
    platform: null,
    type: null,
    version: null,
  },
  createdAt: null,
};

const model = {
  acceptedAgreements: [],
  avatar: null,
  bio: null,
  dob: null,
  email: null,
  enabled: true,
  firstName: null,
  id: null,
  fiscalCode: null,
  lastName: null,
  password: null,
  phone: null,
  platformId: null,
  profileAgreement: [],
  selectedSkills: [],
  userType: null,
  birth_place: null,
  skillDescription: null,
  role: null,
  // Codice referral prioritizzato (query > invito, HDACC-275). register() fa lo
  // spread di `current`, quindi finisce nel payload come RegisterUserIn.referral
  // e il backend crea la ProfileReferral del profilo appena registrato.
  referral: null,
  additionalFields: {
    category_professional: null,
  },
};

const professionalCategories = {
  Surveyor: "Geometra",
  Architect: "Architetto",
  Engineer: "Ingegnere",
  StructuralEngineer: "Ingegnere strutturale",
  BusinessConsultant: "Consulente aziendale"
};

const professionalSkills = {
  RevitArchitecture: "Revit Architecture",
  GestioneAppalti: "Gestione di appalti",
  ArcView: "ArcView",
  PM: "Gestione progetti",
  RedazionePerizie: "Redazione perizie"
};

export const useUser = defineStore("user", {
  state: () => {
    return {
      // If the model evolves into having nested references we avoid gotchas
      current: cloneDeep(model),
      invitation: false,
      list: [],
      refreshToken: null,
      token: null,
      idToken: null,
      roles: [],
      role_id: null,
      userTypes: [
        'Basic',
        'Lead',
        'Agency',
        'DistrictManager',
        'Distributor',
        'Designer',
        'Artisan'
      ],
      professionalCategoryValue: [
        "Surveyor",
        "Architect",
        "Engineer",
        "StructuralEngineer",
        "BusinessConsultant"
      ],
      professionalSkillsValue: [
        "RevitArchitecture",
        "GestioneAppalti",
        "ArcView",
        "PM",
        "RedazionePerizie"
      ],
      configuration: {
        id: null,
        page: null,
        role_id: null,
        invitationManagement: null,
        networkManagement: null,
        networkType: null,
        signContract: null,
        skill: null,
        subscriptionType: null,
        urlRedirect: null,
        steps: {
          id: null,
          configuration_id: null,
          form_id: null,
          title: null,
          subtitle: null,
          order: null,
          webservice: null,
          form: {
            id: null,
            name: null,
            description: null,
            configEntityType: null
          }
        }
      },
      formFields: {
        id: null,
        name: null,
        fields: {
          id: null,
          label: null,
          selectList: null,
          disabled: null,
          order: null,
          validationField: null,
          validationType: null,
          field: {
            id: 1,
            name: null,
            fieldType: null,
            componentType: null,
            validationRules: null,
            validationRegex: null,
            validationUrl: null,
            validationErrors: null,
            placeholder: null,
            dbColumn: null
          }
        }
      }
    };
  },
  actions: {
    setCurrent(item) {
      this.current = merge(this.current, item);
      if (item.additionalField) {
        this.current.additionalFields = merge({}, model.additionalFields, item.additionalField);
      }
    },
    resetCurrent() {
      this.current = cloneDeep(model);
    },
    async getCurrent({ withIdentity = false, id = null } = {}) {
      // withCredentials so the request carries the HttpOnly access_token cookie set on
      // the register response (post-registration cookie auth, PILLARS-2862). Bearer-
      // authenticated users are unaffected: a valid Bearer takes precedence at the gateway.
      const item = await api.users.getDetails({ withIdentity, id }, { withCredentials: true })
      this.setCurrent(item);
      return item;
    },
    async getList() {
      const list = [];
      this.list = list;
    },
    resetList() {
      this.list = [];
    },
    setTokens(keycloak) {
      this.token = keycloak.token
      this.refreshToken = keycloak.refreshToken
      this.idToken = keycloak.idToken
      instance.defaults.headers.Authorization = `Bearer ${keycloak.token}`
      LocalStorage.set(['auth', 'token'], keycloak.token)
      LocalStorage.set(['auth', 'refreshToken'], keycloak.refreshToken)
      LocalStorage.set(['auth', 'idToken'], keycloak.idToken)
    },
    resetTokens() {
      this.token = null
      this.refreshToken = null
      delete instance.defaults.headers.Authorization
      LocalStorage.removeKey(["auth", "token"])
      LocalStorage.removeKey(["auth", "refreshToken"])
      LocalStorage.removeKey(["auth", "idToken"])
    },
    composeAcceptedAgreements() {
      this.current.profileAgreement.forEach(i => {
        this.current.acceptedAgreements.push(i.agreement.id)
      })
    },
    async register(options) {
      if (!this.userTypes.includes(this.current.userType)) {
        throw new TypeError(`${this.current.userType} is not a valid user type. please select a valid user type.`);
      }

      const payload = {
        ...this.current,
        ...options
      };

      // `referral` è opzionale: inviarlo a null significherebbe chiedere al backend
      // di legare il profilo a un referral inesistente, quindi lo si omette del tutto
      // quando non ce n'è uno (HDACC-275).
      if (!payload.referral) {
        delete payload.referral;
      }

      // withCredentials: the browser stores the HttpOnly access_token/refresh_token cookies
      // service-profile sets on the register response, for cross-app/platform use (PILLARS-2862).
      const user = await api.users.register(payload, { withCredentials: true });

      // Brand-new (non-social) signup: authenticate the rest of the registration flow with the
      // Bearer token service-profile returns in the response body. We use the Bearer (not the
      // cookie) because the cookie is only honored on /profile/* — the /platform/* services reply
      // Access-Control-Allow-Origin:* and reject credentialed requests — whereas this Bearer is
      // accepted across ALL services, so every wizard step (Profile/Legal/Skills/Office/
      // Organization/Attachments) authenticates. Social / already-authenticated users keep their
      // existing Keycloak Bearer (which main.js refreshes), so we never overwrite it.
      const beToken = user?.token;
      if (!keycloak.authenticated) {
        if (beToken?.access) {
          keycloak.token = beToken.access;
          keycloak.refreshToken = beToken.refresh ?? keycloak.refreshToken;
          keycloak.idToken = beToken.id ?? keycloak.idToken;
          this.setTokens(keycloak);
        } else {
          // No Bearer returned → fall back to cookie auth for /profile/* (getCurrent/getRoles
          // send withCredentials). Clear any stale Authorization header: the gateway prefers it
          // over the cookie and would 401 on an invalid Bearer instead of using the cookie.
          delete instance.defaults.headers.Authorization;
        }
      }

      return user;
    },


    async updatePartial(payload) {
      const updated = await api.users.update(payload);
      if (updated && typeof updated === "object") {
        this.setCurrent(updated);
      } else {
        await this.getCurrent({ withIdentity: true });
      }
    },

    async logout(goToPage) {
      const page = goToPage || { name: "public-home" };
      this.resetCurrent();
      this.resetTokens();
      await router.push(page);
      // Post-logout redirect: send user back to app public home (not Keycloak login). Base must match router createWebHistory('/account/').
      const redirectUri =
        typeof window !== "undefined" ? `${window.location.origin}/account/public-home` : undefined;
      keycloak.logout(redirectUri ? { redirectUri } : undefined).catch(() => { });
    },
    async getConfiguration(id, unavailableSteps = []) {
      const config = await api.users.getConfiguration(id);

      config.steps = config.steps.filter(
        (step) => !unavailableSteps.includes(step.form.configEntityType)
      );

      if (config.invitationManagement) {
        this.current.invitationManagement = config.invitationManagement;
      }

      return config;
    },
    async setConfiguration(configuration) {
      this.configuration = configuration;
    },
    async getConfigurationFormFields(id) {
      try {
        const formFields = await api.users.getConfigurationFormFields(id);
        if (!formFields) {
          if (this.isLoggedIn) {
            await this.logout();
            return;
          }

          // await router.push({ name: 'placeholder' });
          return;
        }
        return formFields;
      }
      catch (ex) {
        console.error(ex);
        // await router.push({ name: 'placeholder' });
      }
    },
    async setConfigurationFormFields(formFields) {
      this.formFields = formFields;
    },
    async getRoles() {
      // withCredentials on both calls so they authenticate via the HttpOnly cookie in the
      // post-registration flow (ThankYou/Subscribe call this before the SSO redirect).
      // This retus the status for each role
      const { roles: rolesWithStatus } = await api.users.init({}, { withCredentials: true });
      // This return basic data about each role
      const roles = await api.users.getRoles({}, { withCredentials: true })
      roles.forEach(role => {
        const current = rolesWithStatus.find(r => r.id === role.roleId);
        if (!current) {
          throw new Error(`user init fn didn't return all roles that are returned by profile roles`)
        }
        role.status = current.status;
        role.page = current.page;
      })

      this.roles = roles
    },
    async updateAvatar(file) {
      const image = file ?? this.current.avatar;
      const { avatar } = await api.users.updateAvatar(image, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      this.setCurrent({ avatar });
      return avatar;
    },
    async update() {
      // Removing id as it crashes the backend
      const { id, ...rest } = this.current;
      const user = await api.users.update(rest);
      return user;
    },
    async acceptedAgreements(data) {
      const res = await api.users.acceptAgreement(data)
      return res
    },
    async updatePassword() {
      const user = await api.users.updatePassword(this.current);
      return user;
    },
    async updateAvatar(file) {
      let image = file ?? this.current.avatar;
      const { avatar } = await api.users.updateAvatar(image,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      this.setCurrent({ avatar })
      return avatar;
    },
    async getProfileFromToken(token) {
      const profile = await api.landingcontact.getFromToken(token)
      return profile
    },
    async insertSkills() {
      const res = await api.users.insertSkills(this.current.selectedSkills)
      return res
    },
    async removeSkill(id) {
      const res = await api.users.removeSkill(id)
      return res
    },
    async getNetwork() {
      const network = await api.users.getNetwork()
      return network
    },
    async getNetworkDetail(id) {
      const network = await api.users.getNetworkDetail(id)
      return network
    },
    async clearConfigurationFormFields() {
      this.setConfigurationFormFields([]);
    },
    async isValidPassword(data) {
      try {
        await api.users.login(data);

        return true;
      }
      catch {
        return false;
      }
    },
    async requestApprovation(id, params = {}, { as = "FinalUser" } = {}) {
      const res = await api.users.requestAccreditation({},
        {
          headers: {
            "X-AppRole": this.current.role,
          },
        }
      )
      return res
    },
  },
  getters: {
    isLoggedIn: (state) => Boolean(state.current.id),
    professionalCategoryOptions: (state) =>
      state.professionalCategoryValue.map((value) => {
        return {
          text: professionalCategories[value] || value,
          value: value,
        };
      }),
    professionalSkillsOptions: (state) =>
      state.professionalSkillsValue.map((value) => {
        return {
          text: professionalSkills[value] || value,
          value: value,
        };
      }),
    isFinalUser: state => state.roles.some(matchesProperty('roleName', 'FinalUser')),
    nextIncompleteRole: (state) =>
      state.roles.find(matchesProperty("status", "Pending")),
    isSuspended: state => state.roles.some(matchesProperty('status', 'Suspended')), /* if user has at least one role with Suspended */
    isPending: state => state.roles.some(matchesProperty('status', 'Pending')), /* if user has at least one role with Pending */
    isWaiting: state => state.roles.some(matchesProperty('status', 'Waiting')), /* if user has at least one role with Waiting *//* if user has at least one role with Pending */
    isValidation: state => state.roles.some(matchesProperty('status', 'Validation')), /* if user has at least one role with Validation */
    isActive: (state) => {
      let res = state.roles.filter(r =>
        r.status == "Active" && r.roleId != 1 && r.roleId != 13
      );
      //console.log(!!res.length)
      return (!!res.length);
    },
    isCapoHub: (state) => {
      let res = state.roles.filter(r =>
        r.status == "Active" && ["HubHead"].includes(r.roleName)
      )
      return (!!res.length)
    },
    isDesigner: (state) => {
      let res = state.roles.filter(r =>
        r.status == "Active" && ["Designer"].includes(r.roleName)
      )
      return (!!res.length)
    },
    isBackofficeAdmin: (state) => {
      let res = state.roles.filter(r =>
        r.status == "Active" && ["BackofficeAdmin"].includes(r.roleName)
      )
      return (!!res.length)
    },
    isBackofficeUser: (state) => {
      let res = state.roles.filter(r =>
        r.status == "Active" && ["BackofficeUser"].includes(r.roleName)
      )
      return (!!res.length)
    },
    isUserWithRelevantActiveAccess: (state) => {
      if (state.isPending || state.isSuspended) {
        return false;
      }
      return (state.isCapoHub || state.isDesigner || state.isBackofficeAdmin || state.isBackofficeUser)
    },
    getSortedConfigurationFormFields: (state) => {
      if (state.formFields.id === null) {
        return [];
      }
      return state.formFields.fields.sort((a, b) => a.order - b.order);
    }
  },
});
